/* Pure progress maths — no React, no stores — so it is unit-testable
   (tests/unit/stats.test.ts). */
import { addDays } from '@shared/prayer';
import type { AdhkarDay, Goal, PrayerDay, Session, SessionKind } from '@/lib/types';

export const QURAN_KINDS: SessionKind[] = ['read', 'listen', 'recite'];

export function secondsOn(sessions: Session[], dayKey: string, kinds: SessionKind[] = QURAN_KINDS) {
  return sessions.filter((s) => !s.deleted && s.dayKey === dayKey && kinds.includes(s.kind)).reduce((a, s) => a + s.seconds, 0);
}

export function pagesOn(sessions: Session[], dayKey: string) {
  const set = new Set<number>();
  sessions.filter((s) => !s.deleted && s.dayKey === dayKey).forEach((s) => s.pages?.forEach((p) => set.add(p)));
  return set.size;
}

export function pagesSince(sessions: Session[], sinceMs: number) {
  const set = new Set<number>();
  sessions.filter((s) => !s.deleted && s.start >= sinceMs).forEach((s) => s.pages?.forEach((p) => set.add(p)));
  return set;
}

/** Consecutive days (ending today, or yesterday if today isn't done yet) where `hit(day)` holds. */
export function streak(today: string, hit: (dayKey: string) => boolean, max = 3650): { current: number; best: number } {
  let current = 0;
  let d = hit(today) ? today : addDays(today, -1);
  while (current < max && hit(d)) { current++; d = addDays(d, -1); }
  return { current, best: current };
}

export function bestStreak(days: string[]): number {
  const sorted = [...new Set(days)].sort();
  let best = 0, run = 0, prev: string | null = null;
  for (const d of sorted) {
    run = prev && addDays(prev, 1) === d ? run + 1 : 1;
    best = Math.max(best, run);
    prev = d;
  }
  return best;
}

export function lastNDays(today: string, n: number): string[] {
  return Array.from({ length: n }, (_, i) => addDays(today, i - n + 1));
}

export function weekSeries(sessions: Session[], today: string, n = 7) {
  return lastNDays(today, n).map((d) => ({
    dayKey: d,
    read: secondsOn(sessions, d, ['read']) / 60,
    // Older 'recite' sessions (recite mode, before it was merged) count as listening.
    listen: secondsOn(sessions, d, ['listen', 'recite']) / 60,
    adhkar: secondsOn(sessions, d, ['adhkar']) / 60,
  }));
}

export function prayerScore(day: PrayerDay | undefined): number {
  if (!day) return 0;
  return Object.values(day.marks).filter((m) => m === 'ontime' || m === 'jamaah' || m === 'late' || m === 'qada').length;
}

export const adhkarDone = (d: AdhkarDay | undefined) => ({ morning: !!d?.morning?.doneAt, evening: !!d?.evening?.doneAt });

/* ── Goals ──────────────────────────────────────────────────────────── */
export interface GoalProgress {
  /** 0..1 for today's portion */
  today: number;
  /** 0..1 for the whole goal (khatm only) */
  overall?: number;
  todayLabel: string;
  overallLabel?: string;
  dailyTarget: number;
  unit: 'pages' | 'min';
}

const DAY_MS = 86400_000;

export function khatmPlan(goal: Goal, now = Date.now()) {
  const startPage = goal.startPage ?? 1;
  const totalPages = 605 - startPage;
  const elapsedDays = Math.max(0, Math.floor((now - goal.createdAt) / DAY_MS));
  const daysLeft = Math.max(1, goal.target - elapsedDays);
  return { startPage, totalPages, daysLeft, elapsedDays };
}

export function goalProgress(goal: Goal, sessions: Session[], today: string, now = Date.now()): GoalProgress {
  if (goal.kind === 'khatm') {
    const { totalPages, daysLeft } = khatmPlan(goal, now);
    const read = pagesSince(sessions, goal.createdAt).size;
    const todayPages = pagesOn(sessions, today);
    const remaining = Math.max(0, totalPages - read);
    // Spread what's left over the days left, counting today's reading as part of today.
    const perDay = Math.max(1, Math.ceil((remaining + todayPages) / daysLeft));
    return {
      today: Math.min(1, todayPages / perDay),
      overall: Math.min(1, read / totalPages),
      todayLabel: `${todayPages} / ${perDay} pages today`,
      overallLabel: `${read} of ${totalPages} pages · ${daysLeft} day${daysLeft === 1 ? '' : 's'} left`,
      dailyTarget: perDay,
      unit: 'pages',
    };
  }
  if (goal.kind === 'pages') {
    const p = pagesOn(sessions, today);
    return { today: Math.min(1, p / goal.target), todayLabel: `${p} / ${goal.target} pages today`, dailyTarget: goal.target, unit: 'pages' };
  }
  // A legacy 'memorise' goal counts like a minutes goal.
  const m = Math.floor(secondsOn(sessions, today, QURAN_KINDS) / 60);
  return { today: Math.min(1, m / goal.target), todayLabel: `${m} / ${goal.target} min today`, dailyTarget: goal.target, unit: 'min' };
}
